// Copyright 2026. WebPros International GmbH. All rights reserved.

import {
    ComputeResourceVmStatus,
    IVmResponse,
} from 'common/api/resources/ComputeResourceVm';

// A VM in one of these states no longer exists on the CR, or the CR itself is unreachable:
// the backend policy rejects every power action for them (ComputeResourceVm::isAvailable()).
const UNREACHABLE_STATUSES = [
    ComputeResourceVmStatus.NOT_EXIST,
    ComputeResourceVmStatus.UNAVAILABLE,
];

// States in which the libvirt domain may still be active and holding resources, so a forced
// power off is meaningful. `no state` is included because a destroy on an already-shutoff domain
// is a successful no-op that re-syncs the status.
const FORCE_POWER_OFF_STATUSES = [
    ComputeResourceVmStatus.STARTED,
    ComputeResourceVmStatus.PAUSED,
    ComputeResourceVmStatus.NO_STATE,
];

type IVmPowerActionSubject = Pick<IVmResponse, 'status' | 'real_status' | 'is_processing' | 'is_suspended'>;

// A server whose requested deletion failed on its compute resource is frozen on the backend
// until an administrator deletes it again. The computed `status` carries that label; the
// hypervisor `real_status` never does, so the buttons have to look at `status` here.
const isFrozenByFailedDeletion = (vm: IVmPowerActionSubject): boolean =>
    vm.status === ComputeResourceVmStatus.DELETE_FAILED;

const canAct = (vm: IVmPowerActionSubject): boolean =>
    !vm.is_processing && !vm.is_suspended && !isFrozenByFailedDeletion(vm);

/** Start also resumes a paused domain — see the agent's StartWithOptions. */
export const canStartVm = (vm: IVmPowerActionSubject): boolean =>
    canAct(vm)
    && vm.real_status !== ComputeResourceVmStatus.STARTED
    && !UNREACHABLE_STATUSES.includes(vm.real_status);

/** ACPI shutdown/reboot needs a live guest, so these stay started-only. */
export const canGracefullyStopVm = (vm: IVmPowerActionSubject): boolean =>
    canAct(vm) && vm.real_status === ComputeResourceVmStatus.STARTED;

export const canRestartVm = canGracefullyStopVm;

export const canForcePowerOffVm = (vm: IVmPowerActionSubject): boolean =>
    canAct(vm) && FORCE_POWER_OFF_STATUSES.includes(vm.real_status);
